// src/components/OfferpackageOverview.tsx

import React, { useState } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
    Button,
    Box,
    LinearProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetOfferPackages, useDeleteOfferPackage } from "../../hooks/data/data_offerpackage";
import { IOfferPackage } from "../../interfaces/IOfferpackage";
import { OfferpackageEdit } from "./OfferpackageEdit";

export const OfferpackageOverview: React.FC = () => {
    const { offerPackages, isLoadingOfferPackages, errorOfferPackages } = useGetOfferPackages();
    const { deleteOfferPackage } = useDeleteOfferPackage();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState<IOfferPackage | null>(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [toDeleteId, setToDeleteId] = useState<number | null>(null);

    const rows: IOfferPackage[] = offerPackages.map((pkg) => ({
        id: pkg.id,
        name: pkg.name,
        description: pkg.description,
        package_services: pkg.package_services,
        package_materials: pkg.package_materials,
        package_material_defs: pkg.package_material_defs,
    }));

    const handleDeleteClick = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        setToDeleteId(id);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (toDeleteId !== null) {
            try {
                await deleteOfferPackage(toDeleteId);
            } catch (err) {
                console.error("Fehler beim Löschen:", err);
            }
        }
        setConfirmOpen(false);
        setToDeleteId(null);
    };

    const handleCancelDelete = () => {
        setConfirmOpen(false);
        setToDeleteId(null);
    };

    const columns: GridColDef[] = [
        { field: "id", headerName: "ID", width: 80 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
        {
            field: "description",
            headerName: "Beschreibung",
            flex: 2,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) =>
                params.value && String(params.value).trim() !== "" ? (
                    <span>{params.value}</span>
                ) : (
                    <em>Keine Beschreibung vorhanden</em>
                ),
        },
        {
            field: "servicesCount",
            headerName: "Leistungen",
            type: "number",
            width: 130,
            valueGetter: (params) => params.row.package_services.length,
        },
        {
            field: "materialsCount",
            headerName: "Materialien",
            type: "number",
            width: 130,
            valueGetter: (params) => params.row.package_materials.length,
        },
        {
            field: "defsCount",
            headerName: "Kategorien",
            type: "number",
            width: 130,
            valueGetter: (params) => params.row.package_material_defs.length,
        },
        {
            field: "actions",
            headerName: "Aktion",
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title="Löschen">
                    <IconButton size="small" onClick={(e) => handleDeleteClick(e, params.row.id as number)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];


    if (errorOfferPackages) {
        return (
            <Typography color="error">Fehler beim Laden der Angebotspakete: {errorOfferPackages.message}</Typography>
        );
    }

    if (isLoadingOfferPackages) {
        return <LinearProgress />;
    }

    return (
        <Box>
            <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setSelectedPackage(null);
                        setDialogOpen(true);
                    }}
                >
                    Paket erstellen
                </Button>
            </Box>

            <div style={{ width: "100%" }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={isLoadingOfferPackages}
                    autoHeight
                    onRowClick={(params) => {
                        setSelectedPackage(params.row as IOfferPackage);
                        setDialogOpen(true);
                    }}
                />
            </div>

            <OfferpackageEdit open={dialogOpen} initialData={selectedPackage} onClose={() => setDialogOpen(false)} />

            <Dialog open={confirmOpen} onClose={handleCancelDelete}>
                <DialogTitle>Löschen bestätigen</DialogTitle>
                <DialogContent>
                    <Typography>Soll dieses Angebotspaket wirklich gelöscht werden?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>Abbrechen</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
